import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";

const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const PaketRows = ({ kom, rows, kategori, onChange }) => {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const terpakai = new Set(rows.map((r) => r.Kategori_Biaya));
  const nomDump = (r) => (r.Nominal_Dump_Rp === "" || r.Nominal_Dump_Rp == null ? r.Nominal_Rp : r.Nominal_Dump_Rp);
  const total = rows.reduce((s, r) => s + (parseFloat(r.Nominal_Rp) || 0), 0);
  const totalDump = rows.reduce((s, r) => s + (parseFloat(nomDump(r)) || 0), 0);
  const slug = kom.replace(/ /g, "-");
  return (
    <div className="space-y-2" data-testid={`paket-rows-${slug}`}>
      {rows.length > 0 && (
        <div className="hidden sm:grid grid-cols-[1fr_130px_130px_1fr_36px] gap-2 text-[11px] uppercase tracking-wider text-zinc-500 px-1">
          <span>Kategori</span><span>Truck / Tangki</span><span>Dump Truck</span><span>Catatan</span><span />
        </div>
      )}
      {rows.length === 0 && <p className="text-sm text-zinc-400 py-2">Belum ada kategori — tambahkan di bawah.</p>}
      {rows.map((r, i) => (
        <div key={i} className="rounded-md border border-zinc-200 p-2 space-y-2 sm:space-y-0 sm:p-0 sm:border-0 sm:grid sm:grid-cols-[1fr_130px_130px_1fr_36px] sm:gap-2 sm:items-center">
          <div className="flex items-center gap-2 sm:contents">
            <Select value={r.Kategori_Biaya} onValueChange={(v) => update(i, { Kategori_Biaya: v })}>
              <SelectTrigger data-testid={`paket-kategori-${slug}-${i}`} className="bg-white h-9 flex-1"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent className="bg-white">
                {kategori.map((k) => (
                  <SelectItem key={k} value={k} disabled={terpakai.has(k) && k !== r.Kategori_Biaya}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
              className="p-2 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 sm:hidden" aria-label="Hapus">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:contents">
          <Input data-testid={`paket-nominal-${slug}-${i}`} type="number" min="1" step="1" inputMode="numeric" placeholder="Truck/Tangki"
            title="Nominal untuk Truck / Tangki CPO"
            value={r.Nominal_Rp} onChange={(e) => update(i, { Nominal_Rp: e.target.value })} className="bg-white h-9 num-hc" />
          <Input data-testid={`paket-nominal-dump-${slug}-${i}`} type="number" min="1" step="1" inputMode="numeric"
            placeholder={r.Nominal_Rp ? `= ${r.Nominal_Rp}` : "Dump Truck"} title="Nominal untuk Dump Truck (kosong = sama dengan Truck/Tangki)"
            value={r.Nominal_Dump_Rp ?? ""} onChange={(e) => update(i, { Nominal_Dump_Rp: e.target.value })} className="bg-white h-9 num-hc" />
          </div>
          <Input placeholder="Catatan (opsional)" value={r.Catatan || ""} onChange={(e) => update(i, { Catatan: e.target.value })}
            className="bg-white h-9 hidden sm:block" />
          <button type="button" data-testid={`paket-hapus-${slug}-${i}`} onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            className="p-2 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 hidden sm:block">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <Button type="button" size="sm" variant="outline" data-testid={`paket-tambah-${slug}`}
          disabled={rows.length >= kategori.length}
          onClick={() => onChange([...rows, { Kategori_Biaya: kategori.find((k) => !terpakai.has(k)) || "", Nominal_Rp: "", Nominal_Dump_Rp: "", Catatan: "" }])}
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
          <Plus className="w-4 h-4 mr-1" /> Tambah Kategori
        </Button>
        <div className="text-sm tabular-nums text-right">
          <span data-testid={`paket-total-${slug}`} className="block sm:inline font-semibold text-emerald-800">Truck/Tangki: {fmtRp(total)}</span>
          <span className="mx-2 text-zinc-300 hidden sm:inline">|</span>
          <span data-testid={`paket-total-dump-${slug}`} className="block sm:inline font-semibold text-amber-800">Dump Truck: {fmtRp(totalDump)}</span>
        </div>
      </div>
    </div>
  );
};

export const PaketBiayaDialog = ({ open, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [aktif, setAktif] = useState("CPO");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/paket-biaya").then(({ data }) => setData(data)).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, [open]);

  const simpan = async () => {
    const paket = {};
    for (const kom of data.komoditas) {
      const rows = data.paket[kom] || [];
      if (rows.some((r) => !r.Kategori_Biaya || !(parseFloat(r.Nominal_Rp) > 0))) {
        toast.error(`Lengkapi kategori & nominal pada paket ${kom}`);
        setAktif(kom);
        return;
      }
      if (rows.some((r) => r.Nominal_Dump_Rp !== "" && r.Nominal_Dump_Rp != null && !(parseFloat(r.Nominal_Dump_Rp) > 0))) {
        toast.error(`Nominal Dump Truck pada paket ${kom} harus lebih dari 0 (atau kosongkan)`);
        setAktif(kom);
        return;
      }
      paket[kom] = rows.map((r) => ({
        ...r, Nominal_Rp: parseFloat(r.Nominal_Rp),
        Nominal_Dump_Rp: r.Nominal_Dump_Rp === "" || r.Nominal_Dump_Rp == null ? null : parseFloat(r.Nominal_Dump_Rp),
      }));
    }
    setSaving(true);
    try {
      await api.put("/paket-biaya", { paket });
      toast.success("Paket biaya tersimpan");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="paket-biaya-dialog" className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="w-4 h-4 text-emerald-700" /> Paket Biaya per Komoditas</DialogTitle>
          <DialogDescription>
            Atur kategori biaya & nominal standar sekali, lalu terapkan ke banyak trip sekaligus dari Daftar Trip.
            Nominal bisa dibedakan untuk <b>Truck/Tangki</b> dan <b>Dump Truck</b> — sistem memilih otomatis sesuai jenis truk dari plat trip.
            Kolom Dump Truck yang dikosongkan memakai nilai Truck/Tangki. Paket juga <b>diterapkan otomatis</b> saat status trip pertama kali menjadi <b>Berangkat</b> (bisa dimatikan di menu Notifikasi → Pengaturan). Tanggal keluar mengikuti Tanggal Muat, status langsung <b>Sudah Dicairkan</b>; kategori yang sudah ada dilewati.
          </DialogDescription>
        </DialogHeader>
        {data === null ? (
          <div className="flex justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex rounded-md border border-zinc-300 overflow-hidden" data-testid="paket-tabs">
              {data.komoditas.map((kom) => (
                <button key={kom} type="button" data-testid={`paket-tab-${kom.replace(/ /g, "-")}`} onClick={() => setAktif(kom)}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${aktif === kom ? "bg-emerald-700 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
                  {kom} <span className="text-xs opacity-80">({(data.paket[kom] || []).length})</span>
                </button>
              ))}
            </div>
            <PaketRows kom={aktif} rows={data.paket[aktif] || []} kategori={data.kategori}
              onChange={(rows) => setData({ ...data, paket: { ...data.paket, [aktif]: rows } })} />
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button type="button" variant="outline" onClick={onClose} data-testid="paket-batal">Batal</Button>
              <Button type="button" onClick={simpan} disabled={saving} data-testid="paket-simpan"
                className="bg-emerald-700 hover:bg-emerald-800 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Simpan Paket
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
